/**
 * Bank Account Dialog Component
 *
 * Modal dialog for creating and editing gaushala bank account details
 * Supports both create and edit modes with full validation
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import {
  bankAccountService,
  BankAccountResponse,
  CreateBankAccountRequest,
  UpdateBankAccountRequest,
} from '@/services/gaushala/bankAccountService';

interface BankAccountDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  existingAccount?: BankAccountResponse;
  onSuccess: (account: BankAccountResponse) => void;
}

interface FormData {
  accountHolderName: string;
  accountNumber: string;
  ifscCode: string;
  bankName: string;
  branchName: string;
  accountType: string;
  email: string;
  phone: string;
}

interface FormErrors {
  [key: string]: string;
}

export const BankAccountDialog: React.FC<BankAccountDialogProps> = ({
  isOpen,
  onOpenChange,
  mode,
  existingAccount,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FormData>({
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    bankName: '',
    branchName: '',
    accountType: 'SAVINGS',
    email: '',
    phone: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAccountNumber, setShowAccountNumber] = useState(false);

  /**
   * Initialize form whenever the dialog opens.
   * - 'create' mode: ALWAYS empty.
   * - 'edit' mode: pre-fill EVERY field from existingAccount, including the masked
   *   account number (e.g., "****2222"). Submit logic skips accountNumber if the user
   *   left the masked value untouched, so it acts as "no change".
   */
  useEffect(() => {
    if (!isOpen) return;

    if (mode === 'edit' && existingAccount) {
      setFormData({
        accountHolderName: existingAccount.accountHolderName || '',
        accountNumber: existingAccount.maskedAccountNumber || '',
        ifscCode: existingAccount.ifscCode || '',
        bankName: existingAccount.bankName || '',
        branchName: existingAccount.branchName || '',
        accountType: existingAccount.accountType || 'SAVINGS',
        email: existingAccount.email || '',
        phone: existingAccount.phone || '',
      });
      setShowAccountNumber(true); // show masked value as-is (the stars are not sensitive)
    } else {
      setFormData({
        accountHolderName: '',
        accountNumber: '',
        ifscCode: '',
        bankName: '',
        branchName: '',
        accountType: 'SAVINGS',
        email: '',
        phone: '',
      });
      setShowAccountNumber(false);
    }
    setErrors({});
  }, [isOpen, mode, existingAccount]);

  /** True when the user has left the masked placeholder untouched in edit mode. */
  const isAccountNumberUnchanged = (): boolean =>
    mode === 'edit' &&
    !!existingAccount &&
    formData.accountNumber === (existingAccount.maskedAccountNumber || '');

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Account holder name validation — alphabets, spaces and dots only
    if (!formData.accountHolderName.trim()) {
      newErrors.accountHolderName = 'Account holder name is required';
    } else if (formData.accountHolderName.trim().length < 2 || formData.accountHolderName.length > 200) {
      newErrors.accountHolderName = 'Name must be between 2 and 200 characters';
    } else if (!/^[A-Za-z][A-Za-z.\s]*$/.test(formData.accountHolderName.trim())) {
      newErrors.accountHolderName = 'Name can contain letters, spaces and dots only';
    }

    // Account number validation — required in BOTH create and edit modes.
    // In edit mode the masked placeholder (e.g., "****2222") counts as a valid "no change" value.
    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = 'Account number is required';
    } else if (!isAccountNumberUnchanged() && !/^\d{9,18}$/.test(formData.accountNumber.trim())) {
      newErrors.accountNumber = 'Account number must be 9-18 digits';
    }

    // IFSC code validation
    if (!formData.ifscCode.trim()) {
      newErrors.ifscCode = 'IFSC code is required';
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode)) {
      newErrors.ifscCode = 'IFSC format: 4 letters + 0 + 6 alphanumeric (e.g., SBIN0001234)';
    }

    // Bank name validation
    if (!formData.bankName.trim()) {
      newErrors.bankName = 'Bank name is required';
    } else if (formData.bankName.length < 2 || formData.bankName.length > 200) {
      newErrors.bankName = 'Bank name must be between 2 and 200 characters';
    }

    // Branch name validation (optional but if provided should be valid)
    if (formData.branchName && formData.branchName.length > 200) {
      newErrors.branchName = 'Branch name must not exceed 200 characters';
    }

    // Email validation (optional but if provided should be valid)
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation (optional but if provided should be 10 digits)
    if (formData.phone && !/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = 'Phone number must be 10 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      setSubmitting(true);

      if (mode === 'create') {
        // If an account already exists, PUT (replace); otherwise POST (create new).
        if (existingAccount) {
          const updateRequest: UpdateBankAccountRequest = {
            accountHolderName: formData.accountHolderName.trim(),
            accountNumber: formData.accountNumber.trim(),
            ifscCode: formData.ifscCode.trim(),
            bankName: formData.bankName.trim(),
            branchName: formData.branchName.trim(),
            accountType: formData.accountType,
            email: formData.email.trim(),
            phone: formData.phone,
          };

          const response = await bankAccountService.updateBankAccount(updateRequest);
          toast.success('Bank account updated successfully!');
          onSuccess(response);
          onOpenChange(false);
        } else {
          const request: CreateBankAccountRequest = {
            accountHolderName: formData.accountHolderName.trim(),
            accountNumber: formData.accountNumber.trim(),
            ifscCode: formData.ifscCode.trim(),
            bankName: formData.bankName.trim(),
            ...(formData.branchName && { branchName: formData.branchName.trim() }),
            accountType: formData.accountType,
            ...(formData.email && { email: formData.email.trim() }),
            ...(formData.phone && { phone: formData.phone }),
          };

          const response = await bankAccountService.createBankAccount(request);
          toast.success('Bank account created successfully!');
          onSuccess(response);
          onOpenChange(false);
        }
      } else {
        const request: UpdateBankAccountRequest = {};

        if (formData.accountHolderName) {
          request.accountHolderName = formData.accountHolderName.trim();
        }
        // Only send accountNumber if user actually changed it from the masked placeholder
        if (formData.accountNumber && !isAccountNumberUnchanged()) {
          request.accountNumber = formData.accountNumber.trim();
        }
        if (formData.ifscCode) {
          request.ifscCode = formData.ifscCode.trim();
        }
        if (formData.bankName) {
          request.bankName = formData.bankName.trim();
        }
        if (formData.branchName) {
          request.branchName = formData.branchName.trim();
        }
        if (formData.accountType) {
          request.accountType = formData.accountType;
        }
        if (formData.email) {
          request.email = formData.email.trim();
        }
        if (formData.phone) {
          request.phone = formData.phone;
        }

        const response = await bankAccountService.updateBankAccount(request);
        toast.success('Bank account updated successfully!');
        onSuccess(response);
        onOpenChange(false);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save bank account';
      toast.error(errorMessage);
      console.error('Error saving bank account:', error);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle input change
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let processedValue = value;

    // For phone field, only allow digits
    if (name === 'phone') {
      processedValue = value.replace(/\D/g, '').slice(0, 10);
    }

    // For account number field, only allow digits.
    // In edit mode the field starts pre-filled with the masked value (e.g., "****2222") —
    // when the user types, we strip the asterisks so the new digits replace the mask cleanly.
    if (name === 'accountNumber') {
      processedValue = value.replace(/\D/g, '').slice(0, 18);
    }

    // Alphabet-only fields: letters, spaces and dots only — no digits or special chars.
    // Matches the city/country style validation used elsewhere in the app.
    if (name === 'accountHolderName' || name === 'bankName' || name === 'branchName') {
      processedValue = value.replace(/[^A-Za-z.\s]/g, '').slice(0, 200);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  /**
   * Handle select change
   */
  const handleSelectChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      accountType: value,
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'create'
              ? existingAccount
                ? '➕ Add New Bank Account'
                : '➕ Link Bank Account'
              : '✏️ Update Bank Account'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? existingAccount
                ? `Enter new bank account details. This will replace the current linked account (${existingAccount.maskedAccountNumber}).`
                : 'Add your bank account details for payment processing'
              : 'Update your bank account information'}
          </DialogDescription>
        </DialogHeader>

        {/* Status Alert for Edit Mode */}
        {mode === 'edit' && existingAccount && (
          <div className="mb-4">
            <Alert className={existingAccount.isVerified ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {existingAccount.isVerified ? (
                  <span className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-green-800">✓ Account verified</span>
                  </span>
                ) : (
                  <span className="text-yellow-800">⚠️ Account pending verification</span>
                )}
              </AlertDescription>
            </Alert>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
          {/* Decoy fields to absorb Chrome autofill (hidden, not part of formData) */}
          <input
            type="text"
            name="prevent-autofill-username"
            autoComplete="username"
            tabIndex={-1}
            aria-hidden="true"
            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
          />
          <input
            type="password"
            name="prevent-autofill-password"
            autoComplete="new-password"
            tabIndex={-1}
            aria-hidden="true"
            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
          />

          {/* Account Holder Name */}
          <div className="space-y-2">
            <Label htmlFor="accountHolderName" className="font-medium">
              Account Holder Name *
            </Label>
            <Input
              id="accountHolderName"
              name="accountHolderName"
              placeholder="Enter account holder name"
              value={formData.accountHolderName}
              onChange={handleInputChange}
              disabled={submitting}
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
              className={errors.accountHolderName ? 'border-red-500' : ''}
            />
            {errors.accountHolderName && (
              <p className="text-sm text-red-500">{errors.accountHolderName}</p>
            )}
          </div>

          {/* Account Number with Show/Hide Toggle */}
          <div className="space-y-2">
            <Label htmlFor="accountNumber" className="font-medium">
              Account Number *
            </Label>
            <div className="relative">
              <Input
                id="accountNumber"
                name="accountNumber"
                placeholder={mode === 'edit' ? 'Leave blank to keep current account' : 'Enter 9-18 digit account number'}
                value={formData.accountNumber}
                onChange={handleInputChange}
                onFocus={(e) => {
                  // In edit mode, if the field still holds the masked placeholder, select it
                  // so the user's first keypress overwrites the mask cleanly.
                  if (mode === 'edit' && isAccountNumberUnchanged()) {
                    e.target.select();
                  }
                }}
                disabled={submitting}
                maxLength={18}
                autoComplete="new-password"
                data-1p-ignore
                data-lpignore="true"
                data-form-type="other"
                className={`pr-10 ${errors.accountNumber ? 'border-red-500' : ''}`}
                // In edit mode the masked value (****2222) is plain text — show as text so
                // the asterisks render. In create mode keep password masking until toggled.
                type={mode === 'edit' || showAccountNumber ? 'text' : 'password'}
                inputMode={mode === 'edit' && isAccountNumberUnchanged() ? undefined : 'numeric'}
              />
              {mode === 'create' && formData.accountNumber && (
                <button
                  type="button"
                  onClick={() => setShowAccountNumber(!showAccountNumber)}
                  disabled={submitting}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                  aria-label={showAccountNumber ? 'Hide account number' : 'Show account number'}
                >
                  {showAccountNumber ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              )}
            </div>
            {mode === 'create' && existingAccount && (
              <p className="text-xs text-gray-500">
                Current linked account: <span className="font-mono">{existingAccount.maskedAccountNumber}</span> — submitting will replace it with the new details.
              </p>
            )}
            {mode === 'edit' && (
              <p className="text-xs text-gray-500">
                {isAccountNumberUnchanged()
                  ? `Showing current masked account. Keep it as-is to leave unchanged, or click and type a new 9–18 digit number to replace it.`
                  : formData.accountNumber
                    ? '✓ New account number entered — this will replace the current one on update'
                    : '⚠ Account number is required. Type a new 9–18 digit number, or close and reopen to restore the current masked value.'}
              </p>
            )}
            {errors.accountNumber && (
              <p className="text-sm text-red-500">{errors.accountNumber}</p>
            )}
          </div>

          {/* IFSC Code */}
          <div className="space-y-2">
            <Label htmlFor="ifscCode" className="font-medium">
              IFSC Code *
            </Label>
            <Input
              id="ifscCode"
              name="ifscCode"
              placeholder="e.g., SBIN0001234"
              value={formData.ifscCode}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  ifscCode: e.target.value.toUpperCase(),
                }))
              }
              disabled={submitting}
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
              className={errors.ifscCode ? 'border-red-500' : ''}
            />
            {errors.ifscCode && (
              <p className="text-sm text-red-500">{errors.ifscCode}</p>
            )}
          </div>

          {/* Bank Name */}
          <div className="space-y-2">
            <Label htmlFor="bankName" className="font-medium">
              Bank Name *
            </Label>
            <Input
              id="bankName"
              name="bankName"
              placeholder="e.g., State Bank of India"
              value={formData.bankName}
              onChange={handleInputChange}
              disabled={submitting}
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
              className={errors.bankName ? 'border-red-500' : ''}
            />
            {errors.bankName && (
              <p className="text-sm text-red-500">{errors.bankName}</p>
            )}
          </div>

          {/* Branch Name & Account Type (Two columns) */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="branchName" className="font-medium">
                Branch Name
              </Label>
              <Input
                id="branchName"
                name="branchName"
                placeholder="e.g., Main Branch"
                value={formData.branchName}
                onChange={handleInputChange}
                disabled={submitting}
                autoComplete="off"
                data-1p-ignore
                data-lpignore="true"
                data-form-type="other"
                className={errors.branchName ? 'border-red-500' : ''}
              />
              {errors.branchName && (
                <p className="text-sm text-red-500">{errors.branchName}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="accountType" className="font-medium">
                Account Type
              </Label>
              <Select value={formData.accountType} onValueChange={handleSelectChange}>
                <SelectTrigger disabled={submitting}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SAVINGS">Savings</SelectItem>
                  <SelectItem value="CURRENT">Current</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Email & Phone (Two columns) */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-medium">
                Email (Optional)
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="e.g., info@gaushala.com"
                value={formData.email}
                onChange={handleInputChange}
                disabled={submitting}
                autoComplete="off"
                data-1p-ignore
                data-lpignore="true"
                data-form-type="other"
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && (
                <p className="text-sm text-red-500">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="font-medium">
                Phone (Optional)
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                inputMode="numeric"
                placeholder="10-digit phone number"
                maxLength={10}
                value={formData.phone}
                onChange={handleInputChange}
                disabled={submitting}
                autoComplete="off"
                data-1p-ignore
                data-lpignore="true"
                data-form-type="other"
                className={errors.phone ? 'border-red-500' : ''}
              />
              {errors.phone && (
                <p className="text-sm text-red-500">{errors.phone}</p>
              )}
            </div>
          </div>
        </form>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {mode === 'create'
                  ? existingAccount ? 'Saving...' : 'Creating...'
                  : 'Updating...'}
              </>
            ) : (
              <>
                {mode === 'create'
                  ? existingAccount ? 'Save Account' : 'Create Account'
                  : 'Update Account'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BankAccountDialog;
